import { useEffect, useRef, useState, type ReactNode } from 'react';
import { abmelden, anmelden, liegtOben } from './fensterstapel.ts';

// ============================================================================
// Formularteile. Einmal gebaut, in jedem Modul dieselben.
//
// Drei Dinge, die jedes Formular hier kann und die man sonst in jedem
// einzelnen nachbaut und dabei jedes Mal etwas anders macht:
//
//   1. Es zeigt den Fehler AM FELD, nicht als Satz über dem Ganzen. Die API
//      gibt zu jedem Fachfehler mit, welches Feld gemeint ist.
//   2. Es merkt, ob etwas geändert wurde. Ein Speichern-Knopf, der immer
//      leuchtet, sagt nichts.
//   3. Es gibt den geladenen Stand beim Speichern zurück. Damit erkennt der
//      Server, wenn in der Zwischenzeit jemand anderes geschrieben hat.
// ============================================================================

export interface Fehlerlage {
  text: string;
  feld?: string;
  hinweis?: string;
}

/** Aus einem Fehler der API eine Fehlerlage machen. */
export function ausFehler(f: any): Fehlerlage {
  return {
    text: f?.message ?? 'Unbekannter Fehler.',
    feld: f?.daten?.feld,
    hinweis: f?.daten?.hinweis,
  };
}

export function Fehlerkasten({ lage }: { lage: Fehlerlage | null }) {
  if (!lage) return null;
  return (
    <div className="hinweis fehler">
      {lage.text}
      {/* EIN HINWEIS DARF ZEILEN HABEN. Steht darin etwas zum Abtippen - ein
          Befehl für den Server, eine Zeile für die .env -, hängt es ohne
          `pre-line` in einer einzigen Wurst, und wer drei Befehle in einer
          Zeile liest, tippt den zweiten falsch ab. Für jeden anderen Hinweis
          ändert die Regel nichts: ohne Zeilenumbruch im Text bricht auch
          `pre-line` nichts um. */}
      {lage.hinweis && (
        <div className="klein"
             style={{ marginTop: 4, fontWeight: 400, whiteSpace: 'pre-line' }}>
          {lage.hinweis}
        </div>
      )}
    </div>
  );
}


// ------------------------------------------------------------------- Felder

interface FeldGemeinsam {
  name: string;
  titel: string;
  hinweis?: string;
  pflicht?: boolean;
  breite?: number | string;
  fehler?: Fehlerlage | null;
  gesperrt?: boolean;
}

function Umschlag({
  name, titel, hinweis, pflicht, fehler, breite, children,
}: FeldGemeinsam & { children: ReactNode }) {
  const rot = fehler?.feld === name;
  return (
    <div className="feld" style={{ width: breite ?? 260 }}>
      <label htmlFor={name}>
        {titel}
        {pflicht && <span className="pflicht" title="Pflichtfeld"> *</span>}
      </label>
      {children}
      {rot && <div className="feldfehler">{fehler!.text}</div>}
      {!rot && hinweis && <div className="feldhinweis">{hinweis}</div>}
    </div>
  );
}

export function Text(
  p: FeldGemeinsam & {
    wert: string | null | undefined;
    setze: (w: string) => void;
    art?: 'text' | 'email' | 'tel' | 'date' | 'time' | 'number';
    mehrzeilig?: boolean;
    /** .585: Zeilen eines mehrzeiligen Felds (sonst 4). */
    zeilen?: number;
    platzhalter?: string;
    /**
     * WIE VIELE ZEICHEN DAS FELD ÜBERHAUPT ANNIMMT.
     *
     * ORIGINALTEXT MARVIN (15.09.2026, Bild 11)
     *   „trage das auch bei den tiefbauern in den feldern ein als maximale
     *    eingabe."
     *
     * **ES IST EIN RIEGEL AM FELD UND KEINE PRÜFUNG DANACH.** Wer 48 Zeichen
     * tippen kann und beim Speichern eine rote Meldung bekommt, hat 48
     * Zeichen formuliert und muss von vorn anfangen; wer bei 40 nicht weiter
     * tippen kann, formuliert kürzer, während er schreibt. Der Server weist
     * trotzdem ab - eine Grenze, die nur im Browser steht, ist keine.
     *
     * DIE RESTZAHL STEHT DANEBEN, SOBALD ES ENG WIRD. Ein Feld, das einfach
     * aufhört, Zeichen anzunehmen, sieht aus wie eine klemmende Tastatur.
     */
    hoechstens?: number;
    /** Werte zum Auswählen, frei überschreibbar (eine Liste unter dem Feld). */
    vorschlaege?: string[];
  }
) {
  const rot = p.fehler?.feld === p.name;
  const laenge = (p.wert ?? '').length;
  const rest = p.hoechstens ? p.hoechstens - laenge : null;
  const gemein = {
    id: p.name,
    value: p.wert ?? '',
    disabled: p.gesperrt,
    placeholder: p.platzhalter,
    maxLength: p.hoechstens,
    className: rot ? 'falsch' : undefined,
    onChange: (e: any) => p.setze(e.target.value),
  };
  /* EIN MEHRZEILIGES FELD NIMMT DIE VOLLE BREITE, OHNE DASS ES JEDE
     AUFRUFSTELLE EINZELN SAGT.
   *
   * ORIGINALTEXT MARVIN (03.09.2026, Bildschirmfoto „Rahmenvertrag ändern")
   *   „hier ist die bemerkung unnötig abgeschnitten. nutze die volle breite!"
   *
   * `Umschlag` gibt jedem Feld 260 Pixel. Für eine Zeile ist das richtig; für
   * einen Absatz ist es eine Spalte, in der vier Wörter stehen und der Rest
   * unsichtbar bleibt. Bis hierher stand `breite="100%"` DREIZEHNMAL von Hand
   * an mehrzeiligen Feldern - und an einem knappen Dutzend anderer fehlte es.
   * **Eine Zahl, die dreizehnmal dasteht, ist dreizehn Gelegenheiten, sie zu
   * vergessen**; dieselbe Lehre wie beim Seitenrand und bei der
   * Schriftgrösse.
   *
   * Der Standard ist jetzt die volle Breite, die feste Zahl ist die Ausnahme:
   * eine ausdrücklich gesetzte `breite` schlägt ihn weiterhin (die
   * Behördenmappe braucht das, dort hängt es an der Art des Platzhalters). */
  return (
    <Umschlag {...p} breite={p.breite ?? (p.mehrzeilig ? '100%' : undefined)}>
      {p.mehrzeilig
        ? <textarea {...gemein} rows={p.zeilen ?? 4} />
        : <input type={p.art ?? 'text'} {...gemein} list={p.vorschlaege?.length ? `${p.name}_liste` : undefined} />}
      {!p.mehrzeilig && !!p.vorschlaege?.length && (
        <datalist id={`${p.name}_liste`}>{p.vorschlaege.map((v) => <option key={v} value={v} />)}</datalist>
      )}
      {/* ERST AB DEM VORLETZTEN VIERTEL. Eine Restzahl, die von Anfang an
          dasteht, ist bei einem Feld, in das 3 Wörter kommen, Fläche - und ab
          dem 2. Mal liest sie niemand mehr. Sie meldet sich, wenn es knapp
          wird, und sagt bei 0 ausdrücklich, dass hier Schluss ist: sonst
          sieht das stumme Feld nach einem Fehler aus. */}
      {rest !== null && rest <= Math.max(10, Math.floor(p.hoechstens! / 4)) && (
        <div className="klein leise">
          {rest > 0
            ? `noch ${rest} Zeichen`
            : `${p.hoechstens} Zeichen, mehr nimmt das Feld nicht`}
        </div>
      )}
    </Umschlag>
  );
}

/**
 * Eine Auswahl, wahlweise mit GRUPPEN.
 *
 * ORIGINALTEXT MARVIN (31.08.2026)
 *   „sowohl beim tiefbau als auch bei uns im backoffice sollen die am
 *    häufigsten verwendeten psotionen nach oben zusammengefasst werden"
 *
 * `gruppe` an einer Option macht daraus ein `optgroup`. Die REIHENFOLGE der
 * Gruppen ist die der Optionen: die erste Option einer Gruppe bestimmt, wo
 * die Gruppe steht. Eine zweite Liste „so heissen die Gruppen der Reihe nach"
 * wäre die naheliegende Antwort und die falsche - sie liefe beim ersten
 * Nachtrag mit der Optionenliste auseinander.
 *
 * KEIN EIGENES BAUTEIL DANEBEN. Ein zweites „AuswahlMitGruppen" wäre dieselbe
 * Sache zweimal, und beim nächsten Umbau bekäme nur eines davon die
 * Verbesserung.
 */
export function Auswahl(
  p: FeldGemeinsam & {
    wert: string | null | undefined;
    setze: (w: string) => void;
    optionen: { wert: string; text: string; gruppe?: string }[];
    leerText?: string;
  }
) {
  const rot = p.fehler?.feld === p.name;
  const gruppen: string[] = [];
  for (const o of p.optionen) {
    const g = o.gruppe ?? '';
    if (!gruppen.includes(g)) gruppen.push(g);
  }
  const hatGruppen = gruppen.some((g) => g !== '');
  return (
    <Umschlag {...p}>
      <select
        id={p.name} value={p.wert ?? ''} disabled={p.gesperrt}
        className={rot ? 'falsch' : undefined}
        onChange={(e) => p.setze(e.target.value)}
      >
        <option value="">{p.leerText ?? 'bitte wählen'}</option>
        {!hatGruppen
          ? p.optionen.map((o) => (
              <option key={o.wert} value={o.wert}>{o.text}</option>
            ))
          : gruppen.map((g) => {
              const drin = p.optionen.filter((o) => (o.gruppe ?? '') === g);
              if (!g) {
                return drin.map((o) => (
                  <option key={o.wert} value={o.wert}>{o.text}</option>
                ));
              }
              return (
                <optgroup key={g} label={g}>
                  {drin.map((o) => (
                    <option key={o.wert} value={o.wert}>{o.text}</option>
                  ))}
                </optgroup>
              );
            })}
      </select>
    </Umschlag>
  );
}

export function Haken(
  p: { name: string; titel: string; wert: boolean; setze: (w: boolean) => void;
       hinweis?: string; gesperrt?: boolean }
) {
  return (
    <div className="feld" style={{ width: 'auto' }}>
      <label style={{ display: 'flex', gap: 8, alignItems: 'center',
                      fontWeight: 400, cursor: 'pointer' }}>
        <input type="checkbox" checked={p.wert} disabled={p.gesperrt}
               onChange={(e) => p.setze(e.target.checked)} />
        {p.titel}
      </label>
      {p.hinweis && <div className="feldhinweis">{p.hinweis}</div>}
    </div>
  );
}

/**
 * Mehrere Haken zu einer Liste.
 *
 * Für Felder, die mehrere Werte gleichzeitig tragen (status_tk). Eine
 * Mehrfachauswahl mit gedrueckter Steuerungstaste kann kaum jemand bedienen,
 * und wer es versucht, verliert mit dem naechsten Klick alles bisher Gewaehlte.
 *
 * Die REIHENFOLGE der Antwort ist die der Liste, nicht die des Anklickens:
 * sonst sind „Zurückgestellt, In Installation" und „In Installation,
 * Zurückgestellt" zwei verschiedene Werte fuer denselben Zustand. Genau das
 * steht früher, acht Mal.
 */
export function HakenReihe(
  p: {
    titel: string;
    optionen: { wert: string; text: string }[];
    wert: string[];
    setze: (w: string[]) => void;
    hinweis?: string;
    gesperrt?: boolean;
  }
) {
  const an = new Set(p.wert);
  const um = (w: string) => {
    const neu = new Set(an);
    if (neu.has(w)) neu.delete(w); else neu.add(w);
    p.setze(p.optionen.filter((o) => neu.has(o.wert)).map((o) => o.wert));
  };
  return (
    <div className="feld" style={{ width: '100%' }}>
      <label>{p.titel}</label>
      <div className="hakenreihe">
        {p.optionen.map((o) => (
          <label key={o.wert} className={an.has(o.wert) ? 'an' : undefined}>
            <input type="checkbox" checked={an.has(o.wert)} disabled={p.gesperrt}
                   onChange={() => um(o.wert)} />
            {o.text}
          </label>
        ))}
        {!p.optionen.length && (
          <span className="klein leise">Liste wird geladen.</span>
        )}
      </div>
      {p.hinweis && <div className="feldhinweis">{p.hinweis}</div>}
    </div>
  );
}

export function Feldreihe({ children }: { children: ReactNode }) {
  return <div className="feldreihe">{children}</div>;
}


// ----------------------------------------------------------------- Der Rahmen

/**
 * Ein Formular mit Speichern und Verwerfen.
 *
 * Der Speichern-Knopf ist aus, solange sich nichts geaendert hat. Und er
 * schaltet sich waehrend des Speicherns ab: zweimal Klicken auf denselben
 * Knopf hat früher mehr als einmal zwei Termine erzeugt.
 */
export function Formular({
  geaendert, laeuft, fehler, speichern, verwerfen, kopf, children, knopfText,
}: {
  geaendert: boolean;
  laeuft: boolean;
  fehler: Fehlerlage | null;
  speichern: () => void;
  verwerfen?: () => void;
  kopf?: ReactNode;
  children: ReactNode;
  knopfText?: string;
}) {
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); if (geaendert && !laeuft) speichern(); }}
    >
      {kopf}
      <Fehlerkasten lage={fehler} />
      {children}
      <div className="reihe" style={{ marginTop: 14 }}>
        <button className="knopf haupt" disabled={!geaendert || laeuft}>
          {laeuft ? 'Einen Moment' : (knopfText ?? 'Speichern')}
        </button>
        {verwerfen && (
          <button type="button" className="knopf" disabled={!geaendert || laeuft}
                  onClick={verwerfen}>
            Verwerfen
          </button>
        )}
        {geaendert && !laeuft && (
          <span className="klein leise">Es gibt ungespeicherte Änderungen.</span>
        )}
      </div>
    </form>
  );
}


/**
 * Ein Dialog.
 *
 * Schliesst mit Escape und mit einem Klick daneben, aber NICHT, waehrend
 * gespeichert wird: ein Dialog, der sich mitten im Schreiben schliesst, laesst
 * den Benutzer im Unklaren, ob es geklappt hat.
 *
 * UND NUR DAS OBERSTE FENSTER HÖRT AUF ESCAPE. Siehe `stapel` darüber.
 */
export function Dialog({
  titel, untertitel, merker, breite, voll, nackt, laeuft, zu, children,
}: {
  titel: string;
  /** Eine Marke neben dem Titel, z. B. „nur intern". */
  merker?: string;
  /** Steht klein unter dem Titel. Meist die Baustelle, damit beim Arbeiten
   *  jederzeit dasteht, an welchem Auftrag man ist. */
  untertitel?: string;
  /** Wunschbreite in Pixeln. Sie wird IMMER gegen 96vw gedeckelt:
   *  ein Fenster, das breiter ist als das Fenster dahinter, hat seinen
   *  rechten Rand ausserhalb, und dort sitzen die Knoepfe. */
  breite?: number;
  /**
   * DAS GANZE BILD, für Fenster, in denen man SIEHT statt tippt.
   *
   * ORIGINALTEXT MARVIN (11.09.2026)
   *   „zudem die dateivorschau beim öffnen muss auf dem kompletten
   *    bildschild sein am PC. so ist das popup zu klein und nimmt die
   *    übersicht weg."
   *
   * Ein Formular hat eine natürliche Breite: eine Zeile, die länger ist als
   * ein Blick, wird schlechter lesbar, nicht besser. Eine ANSICHT hat sie
   * nicht - ein Lageplan auf 1040 Bildpunkten ist ein Lageplan, den man nicht
   * lesen kann. **Wofür ein Fenster da ist, entscheidet seine Breite**, und
   * das ist der Unterschied zwischen den beiden Angaben hier.
   */
  voll?: boolean;
  /**
   * OHNE RAHMEN: nur der Inhalt, kein Kopf, kein weisser Kasten.
   *
   * ORIGINALTEXT MARVIN (15.09.2026)
   *   „bei dem öffnen der fotos brauchen wir keinen Rahmen. Es reicht das
   *    reine Foto ... Bei den pdf Dateien fehlt auch der Zoom. Da kann das
   *    popup auxh ganz weg. Das nimmt nur Größe weg."
   *
   * Der Rahmen ist an einem FORMULAR die Auskunft: er sagt, wo das Fenster
   * anfängt und wo der Knopf zum Schliessen sitzt. Bei einem BILD ist er das
   * Gegenteil - er nimmt genau die Fläche weg, wegen der man es aufmacht.
   *
   * Es bleibt trotzdem ein `Dialog` und wird kein eigener Bau daneben:
   * Escape (nur das oberste Fenster), der Fokus in den Leib und der
   * Fensterstapel sollen an EINER Stelle stehen. Zwei Fassungen laufen beim
   * ersten Nachtrag auseinander, und dann hört ein Fenster auf Escape und
   * das andere nicht.
   */
  nackt?: boolean;
  laeuft?: boolean;
  zu: () => void;
  children: ReactNode;
}) {
  const kasten = useRef<HTMLDivElement>(null);
  const leib = useRef<HTMLDivElement>(null);

  // DER EFFEKT LÄUFT EINMAL, BEIM ÖFFNEN. Er stand einmal auf
  // `[laeuft, zu]`, und `zu` ist an JEDER Aufrufstelle ein an Ort und Stelle
  // gebauter Pfeil (`zu={() => setOffen(false)}`) - also bei jedem Zeichnen
  // ein NEUES Objekt. React vergleicht mit `Object.is`, findet zwei
  // verschiedene Funktionen und lässt den Effekt laufen; der setzt den Fokus
  // neu, und zwar bei JEDEM getippten Zeichen. Marvin am 02.09.2026 zur
  // Wiedervorlage: „bei der wiedervorlage springt die eingabe bei jedem
  // buchstaben immer oben auf schließen."
  // Es ist derselbe Fehler wie in `useEntwurf` (31.08.2026), nur eine Etage
  // weiter: ein an Ort und Stelle gebautes Objekt in einer Abhängigkeitsliste
  // heisst „bei jedem Zeichnen".
  const zuRef = useRef(zu);
  const laeuftRef = useRef(laeuft);
  zuRef.current = zu;
  laeuftRef.current = laeuft;

  useEffect(() => {
    // DIESES FENSTER LIEGT AB JETZT OBEN. Die Regel dazu steht ohne JSX in
    // `fensterstapel.ts`: sie hängt an ALLEN offenen Fenstern zusammen, und
    // hier liesse sie sich nur mit einer Textsuche prüfen.
    const meine = anmelden();

    const taste = (e: KeyboardEvent) => {
      // Gelesen wird aus dem Merker und nicht aus der Umgebung dieses
      // Effekts: er läuft einmal, und ohne Merker hielte er für immer den
      // Stand des ersten Zeichnens fest - Escape schlösse dann auch mitten
      // im Speichern.
      if (e.key !== 'Escape' || laeuftRef.current) return;
      if (!liegtOben(meine)) return;      // ein anderes Fenster liegt darüber
      zuRef.current();
    };
    window.addEventListener('keydown', taste);
    // DER FOKUS GEHÖRT IN DEN LEIB UND NICHT IN DEN KOPF. Gesucht wurde am
    // ganzen Kasten, und der erste anspringbare Knoten in der Reihenfolge des
    // Dokuments ist „schließen" in der Kopfzeile. Wer ein Fenster aufmacht,
    // will schreiben und nicht zumachen.
    const erstes = leib.current?.querySelector<HTMLElement>(
      'input:not([type=hidden]):not([disabled]),select:not([disabled]),'
      + 'textarea:not([disabled]),button:not([disabled])',
    );
    /* .274: am Telefon keine Tastatur beim Öffnen (siehe `sichtbareFlaeche`). */
    if (amTelefon()) leib.current?.focus({ preventScroll: true });
    else erstes?.focus();
    return () => {
      window.removeEventListener('keydown', taste);
      abmelden(meine);
    };
  }, []);

  /* AM TELEFON (.274). Marvin, 01.10.2026: „Ich kann die Seite nicht
     schließen" - das Fenster ging auf, der Fokus sprang ins erste Feld, die
     Tastatur fuhr hoch, und iOS schob das Fenster samt „schließen" oben aus
     dem Bild. Auf einem Gerät ohne Maus bekommt deshalb der Leib den Fokus
     (für Bildschirmleser), aber keine Tastatur. Und weil iOS `dvh` ohne
     Tastatur rechnet, folgt der Grund der wirklich sichtbaren Fläche. */
  useEffect(() => sichtbareFlaeche(), []);

  return (
    <div className={'dialoggrund' + (nackt ? ' nackt' : '')}
         onMouseDown={(e) => {
           if (e.target === e.currentTarget && !laeuft) zu();
         }}>
      <div className={'dialog' + (voll ? ' voll' : '') + (nackt ? ' nackt' : '')}
           style={voll ? undefined
                       : { width: `min(${breite ?? 560}px, 96vw)` }}
           ref={kasten}>
        {!nackt && (
          <div className="dialogkopf">
            <span className="wer">
              <b>{titel}</b>
              {untertitel && <span className="weg">{untertitel}</span>}
            </span>
            {merker && <span className="merker warnung">{merker}</span>}
            <div className="wachsen" />
            <button className="knopf klein" disabled={laeuft} onClick={zu}>
              schließen
            </button>
          </div>
        )}
        <div className="dialogleib" ref={leib} tabIndex={-1}>{children}</div>
      </div>
    </div>
  );
}


/**
 * Haelt einen Entwurf und weiss, ob er sich vom Original unterscheidet.
 *
 * Der geladene Stand (updated_at) wird mitgefuehrt und beim Speichern
 * zurueckgegeben. Damit erkennt der Server, wenn jemand anderes in der
 * Zwischenzeit geschrieben hat, statt dessen Arbeit stillschweigend zu
 * ueberschreiben.
 */
export function useEntwurf<T extends Record<string, any>>(original: T | null) {
  const [entwurf, setEntwurf] = useState<T | null>(original);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);

  /* DER ENTWURF WIRD NACHGEZOGEN, WENN SICH DER INHALT ÄNDERT - NICHT, WENN
   * SICH DIE ADRESSE DES OBJEKTS ÄNDERT.
   *
   * HIER STAND `[original]`, UND DAS HAT DREI FENSTER STUMM GEMACHT. Wer den
   * Haken so schreibt:
   *
   *     const a = useEntwurf<any>({ kls_id: k.kls_id ?? '', ... });
   *
   * gibt bei JEDEM Zeichnen ein NEUES Objekt hinein. React vergleicht die
   * Abhängigkeit mit `Object.is`, findet zwei verschiedene Objekte und lässt
   * den Effekt laufen - der setzt den Entwurf auf das Original zurück, das
   * nächste Zeichnen baut wieder ein neues Objekt, und so fort.
   *
   * **DIE FOLGE WAR EIN FORMULAR, DAS KEINE EINGABE ANNIMMT.** Man tippt ein
   * Zeichen, der Entwurf bekommt es, und noch im selben Wimpernschlag wirft
   * der Effekt es weg. Auf dem Bildschirm sieht das aus, als sei die Tastatur
   * kaputt: das Feld bleibt stehen, „Speichern" bleibt grau, und es gibt
   * keine Fehlermeldung, weil nichts fehlgeschlagen ist.
   *
   * Betroffen waren genau die drei Aufrufstellen, die ein Objekt an Ort und
   * Stelle bauen - „Auftrag bearbeiten" (zweimal) und die Leistungsposition in
   * den Stammdaten. Die beiden Fenster, die eine geladene Zeile
   * durchreichen (`useEntwurf(g)`), waren nie betroffen: dort ist das Original
   * dasselbe Objekt, solange sich die Zeile nicht ändert. **Deshalb ist es nie
   * aufgefallen** - die Hälfte der Formulare arbeitete.
   *
   * KEIN PRÜFSTAND KONNTE DAS SEHEN. Der Übersetzer sieht einen gültigen
   * Haken, die HTTP-Prüfstände schicken ihre Werte am Formular vorbei direkt
   * an den Server, und Prüfstand 014 misst Breiten. Gefunden hat es erst ein
   * Browser, der WIRKLICH getippt hat - und zwar beim Umbau des Fensters, aus
   * einem ganz anderen Anlass.
   *
   * DER ABDRUCK IST DIE ANTWORT: verglichen wird der INHALT. Ein Fenster, in
   * dem jemand gerade schreibt, bekommt seinen Entwurf nur dann neu gesetzt,
   * wenn sich die Daten dahinter wirklich geändert haben. */
  const abdruck = JSON.stringify(original ?? null);
  useEffect(() => { setEntwurf(original); setFehler(null); }, [abdruck]);

  const setze = (feld: keyof T, wert: any) =>
    setEntwurf((e) => (e ? { ...e, [feld]: wert } : e));

  const geaendert = !!original && !!entwurf &&
    Object.keys(entwurf).some((k) => {
      const a = original[k as keyof T];
      const b = entwurf[k as keyof T];
      return (a ?? '') !== (b ?? '');
    });

  /** Nur die Felder, die sich wirklich geaendert haben, plus der Stand. */
  const aenderungen = (): Record<string, any> => {
    if (!original || !entwurf) return {};
    const raus: Record<string, any> = { stand: original.updated_at };
    for (const k of Object.keys(entwurf)) {
      if ((original[k] ?? '') !== (entwurf[k] ?? '')) raus[k] = entwurf[k];
    }
    return raus;
  };

  return {
    entwurf, setze, setEntwurf, geaendert, aenderungen,
    laeuft, setLaeuft, fehler, setFehler,
  };
}


/** Ein Gerät ohne Maus (Telefon, Tablet). */
export function amTelefon(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(hover: none)').matches;
}

/** Die sichtbare Fläche als CSS-Variablen, solange ein Fenster offen ist. */
function sichtbareFlaeche(): () => void {
  const vv = (window as any).visualViewport as VisualViewport | undefined;
  const wurzel = document.documentElement;
  const messen = () => {
    if (!vv) return;
    wurzel.style.setProperty('--sicht-hoehe', `${Math.round(vv.height)}px`);
    wurzel.style.setProperty('--sicht-oben', `${Math.round(vv.offsetTop)}px`);
  };
  messen();
  vv?.addEventListener('resize', messen);
  vv?.addEventListener('scroll', messen);
  return () => {
    vv?.removeEventListener('resize', messen);
    vv?.removeEventListener('scroll', messen);
    if (document.querySelectorAll('.dialoggrund').length <= 1) {
      wurzel.style.removeProperty('--sicht-hoehe');
      wurzel.style.removeProperty('--sicht-oben');
    }
  };
}
